import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  vehicleTypes: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const vehicleTypeSlice = createSlice({
  name: 'vehicleType',
  initialState,
  reducers: {
    setVehicleTypes: (state, action) => {
      state.vehicleTypes = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setVehicleTypes, setTotalCount, setTotalPages, setLoading, setError } = vehicleTypeSlice.actions;

// Get vehicle types (paginated & searchable)
export const getVehicleTypes = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const response = await axiosInstance.get('/admin/vehicle-type/list', {
      params: { page, limit, search }
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setVehicleTypes(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setVehicleTypes([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch vehicle types';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setVehicleTypes([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch vehicle types';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single vehicle type details (prefill edit form)
export const getVehicleTypeDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/vehicle-type/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch vehicle type details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch vehicle type details';
    return { status: false, message: errorMsg };
  }
};

// Add new vehicle type
export const addVehicleType = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/vehicle-type/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add vehicle type' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add vehicle type';
    return { status: false, message: errorMsg };
  }
};

// Update vehicle type details
export const updateVehicleType = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/vehicle-type/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update vehicle type' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update vehicle type';
    return { status: false, message: errorMsg };
  }
};

// Delete vehicle type
export const deleteVehicleType = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/vehicle-type/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete vehicle type' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete vehicle type';
    return { status: false, message: errorMsg };
  }
};

export default vehicleTypeSlice.reducer;
